import type { ReactElement } from "react";
import { cx } from "@/utils/cx";

export const MessageGraphicsVariant = {
	EmptyFolder: "empty-folder",
	NoSearchResults: "no-search-results",
	Map: "map",
	Error: "error",
	EmptyBox: "empty-box",
} as const;

export type MessageGraphicsVariant =
	(typeof MessageGraphicsVariant)[keyof typeof MessageGraphicsVariant];

const GRAPHIC_SRC: Record<MessageGraphicsVariant, string> = {
	[MessageGraphicsVariant.EmptyFolder]: "/images/empty-state/empty-folder.svg",
	[MessageGraphicsVariant.NoSearchResults]:
		"/images/empty-state/no-search-results.svg",
	[MessageGraphicsVariant.Map]: "/images/empty-state/map.svg",
	[MessageGraphicsVariant.Error]: "/images/empty-state/error.svg",
	[MessageGraphicsVariant.EmptyBox]: "/images/empty-state/empty-box.svg",
};

export interface MessageGraphicsProps {
	/**
	 * Illustration variant from Figma `_MessageGraphics`.
	 * @default "empty-folder"
	 */
	variant?: MessageGraphicsVariant;
	className?: string;
}

/**
 * Decorative empty/error-state illustration.
 *
 * Hidden from assistive tech — pair with a visible title that conveys status.
 *
 * @see Figma `_MessageGraphics` 4059:41701
 */
export function MessageGraphics({
	variant = MessageGraphicsVariant.EmptyFolder,
	className,
}: MessageGraphicsProps): ReactElement {
	return (
		<div
			className={cx(
				"relative h-[100px] w-[250px] shrink-0 overflow-clip",
				className,
			)}
			aria-hidden
			data-testid="message-graphics"
			data-variant={variant}
		>
			<img
				src={GRAPHIC_SRC[variant]}
				alt=""
				width={250}
				height={100}
				className="size-full object-contain"
				draggable={false}
			/>
		</div>
	);
}
